import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicBookingService } from '../../services/api';
import { formatApptTime, localDateKey } from '../../utils/dateFormatters';
import './Booking.css';

function dateLabel(key) {
  return new Date(key + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}
function whenLong(iso) {
  return new Date(iso).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit', hour12: true });
}

export default function ManageBooking() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [slots, setSlots] = useState([]);
  const [date, setDate] = useState(null);

  async function load() {
    setLoading(true);
    const r = await publicBookingService.getBooking(token);
    if (r.success) setBooking(r.booking); else setNotFound(true);
    setLoading(false);
  }
  useEffect(() => { load(); }, [token]);

  async function startReschedule() {
    setError(''); setRescheduling(true);
    const r = await publicBookingService.getSlots(booking.service_id);
    setSlots(r.success ? (r.slots || []) : []);
  }

  const slotsByDate = useMemo(() => {
    const map = {};
    for (const s of slots) { const k = localDateKey(s.start); (map[k] ||= []).push(s); }
    return map;
  }, [slots]);
  const availableDates = useMemo(() => Object.keys(slotsByDate).sort(), [slotsByDate]);

  async function applyReschedule(startsAt) {
    setBusy(true); setError('');
    const r = await publicBookingService.rescheduleBooking(token, startsAt);
    setBusy(false);
    if (r.success) { setRescheduling(false); setDate(null); load(); }
    else setError(r.error || 'That time is no longer available.');
  }

  async function cancel() {
    if (!window.confirm('Cancel this booking?')) return;
    setBusy(true);
    const r = await publicBookingService.cancelBooking(token);
    setBusy(false);
    if (r.success) load(); else setError(r.error || 'Could not cancel.');
  }

  if (loading) return <div className="booking-card"><div className="loading-spinner"></div></div>;
  if (notFound) return (
    <div className="booking-card booking-done">
      <h1>Booking not found</h1>
      <p className="text-muted">This link may have expired or is incorrect.</p>
      <Link className="btn btn-primary" to="/book">Make a booking</Link>
    </div>
  );

  const cancelled = booking.status === 'cancelled';

  return (
    <div className="booking-wizard">
      {error && <div className="booking-error">{error}</div>}
      <div className="booking-card">
        <h1>Your booking</h1>
        <p className="manage-service">{booking.service_name} with {booking.crew_names || booking.staff_name}</p>
        <p className="done-when">{whenLong(booking.starts_at)}</p>
        <span className={`status-badge status-${booking.status.replace('_', '-')}`}>{booking.status.replace('_', ' ')}</span>

        {!cancelled && !rescheduling && (
          <div className="done-actions">
            <button className="btn btn-outline" onClick={startReschedule} disabled={busy}>Reschedule</button>
            <button className="btn btn-danger" onClick={cancel} disabled={busy}>Cancel booking</button>
          </div>
        )}

        {cancelled && (
          <div className="done-actions">
            <Link className="btn btn-primary" to="/book">Book again</Link>
          </div>
        )}

        {rescheduling && (
          <div className="reschedule-panel">
            <h2 className="mt-lg">Pick a new time</h2>
            {!date ? (
              availableDates.length === 0 ? <p className="text-muted">No availability right now.</p> : (
                <div className="date-grid">
                  {availableDates.map((k) => <button key={k} className="date-chip" onClick={() => setDate(k)}>{dateLabel(k)}</button>)}
                </div>
              )
            ) : (
              <>
                <p className="text-muted booking-sub">{dateLabel(date)} — <button className="link-btn" onClick={() => setDate(null)}>change day</button></p>
                <div className="time-grid">
                  {(slotsByDate[date] || []).map((s) => (
                    <button key={s.start} className="time-chip" disabled={busy} onClick={() => applyReschedule(s.start)}>{formatApptTime(s.start)}</button>
                  ))}
                </div>
              </>
            )}
            <button className="btn btn-outline mt-md" onClick={() => { setRescheduling(false); setDate(null); }}>Keep current time</button>
          </div>
        )}
      </div>
    </div>
  );
}
